import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { App } from '@capacitor/app';
import { getCheckoutState, clearCheckoutState, isReturningFromCheckout } from '../services/checkoutStateService';

/**
 * Enhanced Stripe return detector that uses multiple strategies:
 * 1. URL parameter detection
 * 2. Stored checkout state
 * 3. App resume detection
 * 4. URL polling for iOS Safari returns
 */
export const useStripeReturnDetector = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);
  const pollInterval = useRef<NodeJS.Timeout>();
  
  useEffect(() => {
    console.log('[StripeReturnDetector] Initializing with location:', location.pathname);
    
    // Function to check and process Stripe return
    const checkAndProcessReturn = () => {
      if (hasProcessed.current) {
        console.log('[StripeReturnDetector] Already processed, skipping');
        return;
      }
      
      // Don't process if we're in the onboarding flow (PaywallScreen handles it)
      if (location.pathname.includes('/onboarding')) {
        console.log('[StripeReturnDetector] In onboarding flow, skipping');
        return;
      }
      
      // Check URL parameters (works for web and sometimes for iOS)
      const urlParams = new URLSearchParams(window.location.search);
      const hashParams = window.location.hash ? 
        new URLSearchParams(window.location.hash.split('?')[1] || '') : 
        null;
      
      const sessionId = urlParams.get('session_id') || hashParams?.get('session_id');
      const type = urlParams.get('type') || hashParams?.get('type');
      
      console.log('[StripeReturnDetector] URL check:', {
        sessionId,
        type,
        url: window.location.href
      });
      
      // Check stored checkout state
      const checkoutState = getCheckoutState();
      
      if (sessionId || checkoutState) {
        console.log('[StripeReturnDetector] Found return evidence:', {
          sessionId,
          checkoutState
        });
        
        // Determine where to navigate
        let targetPath = '';
        
        if (type === 'signup' || checkoutState?.isTemporary === true) {
          // New user signup flow
          const email = urlParams.get('email') || hashParams?.get('email') || checkoutState?.userEmail;
          const plan = urlParams.get('plan') || hashParams?.get('plan') || checkoutState?.plan;
          
          targetPath = `/signup?session_id=${sessionId || 'pending'}&email=${encodeURIComponent(email || '')}&plan=${plan || ''}`;
        } else {
          // Existing user flow
          targetPath = `/app/recipes?session_id=${sessionId || 'complete'}`;
        }
        
        if (targetPath && !location.pathname.includes('/signup') && !location.pathname.includes('/app/recipes')) {
          console.log('[StripeReturnDetector] Navigating to:', targetPath);
          hasProcessed.current = true;
          
          // Clear the checkout state
          clearCheckoutState();
          
          // Navigate to the target
          navigate(targetPath, { replace: true });
        }
      }
    };
    
    // Check immediately
    checkAndProcessReturn();
    
    // Set up polling for iOS Safari returns (check every 500ms for 10 seconds)
    if ((window as any).Capacitor?.isNativePlatform?.()) {
      console.log('[StripeReturnDetector] Starting iOS polling');
      
      let pollCount = 0;
      pollInterval.current = setInterval(() => {
        pollCount++;
        
        if (pollCount > 20) {
          // Stop after 10 seconds
          console.log('[StripeReturnDetector] Stopping polling after 10 seconds');
          if (pollInterval.current) {
            clearInterval(pollInterval.current);
          }
          return;
        }
        
        checkAndProcessReturn();
      }, 500);
    }
    
    // Listen for app resume/state changes
    const handleAppResume = () => {
      console.log('[StripeReturnDetector] App resumed, checking for return');
      
      // Give it a moment for URL to update
      setTimeout(() => {
        checkAndProcessReturn();
      }, 100);
    };
    
    // Listen for URL changes (for web)
    const handleUrlChange = () => {
      console.log('[StripeReturnDetector] URL changed, checking for return');
      checkAndProcessReturn();
    };
    
    // Set up listeners
    let resumeListener: any;
    let stateListener: any;
    
    if ((window as any).Capacitor?.isNativePlatform?.()) {
      App.addListener('resume', handleAppResume).then(l => {
        resumeListener = l;
      });
      
      App.addListener('appStateChange', (state: { isActive: boolean }) => {
        if (state.isActive) {
          handleAppResume();
        }
      }).then(l => {
        stateListener = l;
      });
    }
    
    window.addEventListener('popstate', handleUrlChange);
    
    // Cleanup
    return () => {
      if (pollInterval.current) {
        clearInterval(pollInterval.current);
      }
      if (resumeListener) {
        resumeListener.remove();
      }
      if (stateListener) {
        stateListener.remove();
      }
      window.removeEventListener('popstate', handleUrlChange);
    };
  }, [navigate, location]);
  
  return {
    isReturning: isReturningFromCheckout()
  };
};